import { createContext, FunctionComponent } from 'preact';
import { useContext, useMemo } from 'preact/hooks';

import { DeepWriteable } from '../utils/types';
import { EXPERIMENTS, resolveExperiments } from '../../../config/experiments';
import type { Payload, Experiment, Variant } from '../../../config/experiments';

import { useProvider } from './useProvider';

const ExperimentsContext = createContext<
  Record<string, { id: string; name: string; variant: string }>
>({});

export const ExperimentsProvider: FunctionComponent = ({ children }) => {
  const { configProvider } = useProvider();

  const experiments = useMemo(
    () => resolveExperiments(configProvider),
    [configProvider]
  );

  return (
    <ExperimentsContext.Provider value={experiments}>
      {children}
    </ExperimentsContext.Provider>
  );
};

export type ResolvedExperiment<T extends Experiment = Experiment> = Omit<
  T,
  'variants'
> & {
  variant: Variant<T['variants']>;
  payload: DeepWriteable<Payload<T['variants']>>;
};

export type Experiments = {
  [K in keyof typeof EXPERIMENTS]: ResolvedExperiment<(typeof EXPERIMENTS)[K]>;
};

export const useExperiments = () =>
  useContext(ExperimentsContext) as Experiments;
